import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Paper, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet } from "../../lib";
import { PREOWNED_SITE_URL } from "../../themes";

/**
 * Storefront root the generated HTML points at. The blocks go into marketing
 * email, so the links must be absolute and public — not the admin's host.
 */
const SITE = PREOWNED_SITE_URL || "https://www.prestigetime.com/preowned";

/** Accessories are stocked under their item_id, which starts at 100001. */
const isAccessoryStock = (stockId) => Number(stockId) > 100000;

const esc = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Mirrors WatchModel::getUrl() — see PreownedWatchesPage for the same logic. */
const encodeModelNo = (v) =>
  String(v ?? "").replace(/\//g, "~").replace(/#/g, "`").replace(/ /g, "_");
const slugPart = (v) =>
  encodeURIComponent(
    encodeModelNo(v).replace(/[^A-Za-z0-9 ]/g, "-").replace(/-+/g, "-"),
  );

function itemUrl(it) {
  return (
    `${SITE}/${slugPart(it.brand)}-${slugPart(it.series ?? it.accessory_type)}-` +
    `${encodeModelNo(it.model_no)}-${it.stock_id}`
  );
}

const imageUrl = (it) =>
  `${SITE}/images/watches${it.image_dir}/${it.item_id}/first_img_450.jpg`;

/**
 * The email block, reproducing the CI4 Generator's markup byte for byte —
 * these land in campaigns alongside existing blocks, so the styling has to
 * match. Watches show the circa/condition/box/papers line; accessories show
 * their type instead of a series.
 */
function buildHtml(it, specialText) {
  const url = itemUrl(it);
  const img = imageUrl(it);
  const accessory = it.accessory_type_id != null;
  const subtitle = accessory ? it.accessory_type : it.series || "";
  const detail = accessory
    ? esc(it.model_no)
    : [
        it.show_circa ? "circa" : "",
        it.year,
        it.condition,
        it.box,
        it.papers,
      ]
        .filter((p) => p !== "" && p != null)
        .join(", ")
        .replace(/^circa, /, "circa ");

  return `<table cellpadding="0" cellspacing="0" width="300" border="0" align="left" class="templateColumnContainer">
  <tr>
    <td align="center" valign="top" style="padding-top:20px;"><a href="${url}" target="_blank"><img alt="${esc(it.brand)} ${esc(subtitle)}" src="${img}" style="display:block;" width="280" height="280" border="0" class="img-max columnImage"></a></td>
  </tr>
  <tr>
    <td align="center" style="color:#35643A; font-size:18px; font-weight:600;" class="auto-style1 rightColumnContent">${esc(it.brand)}</td>
  </tr>
  <tr>
    <td align="center" style="font-size: 16px; font-family: Helvetica, Arial, sans-serif; color: #000000; font-weight:200;" class="rightColumnContent">${esc(subtitle)}</td>
  </tr>
  <tr>
    <td align="center" style="font-size: 13px; font-family: Helvetica, Arial, sans-serif; color: #000000; font-weight:300;" class="rightColumnContent">${esc(detail)}</td>
  </tr>
  <tr>
    <td align="center" style="font-size: 14px; font-family: Helvetica, Arial, sans-serif; color: #E50003; font-weight:300; font-style: italic; height: 20px;" class="rightColumnContent">${esc(specialText)}</td>
  </tr>
  <tr>
    <td align="center"><a href="${url}" target="_blank" style="font-size: 15px; font-family: Helvetica, Arial, sans-serif; font-weight: normal; color: #ffffff;  background-color:#666; border: 10px solid #666; border-left-width: 20px; border-right-width: 20px; display: inline-block; text-decoration:none; letter-spacing: .1rem;" class="rightColumnContent">VIEW WATCH</a></td>
  </tr>
</table>`;
}

const FIELD = {
  fontSize: 12,
  height: 24,
  padding: "2px 5px",
  border: "1px solid rgba(0, 0, 0, 0.23)",
  borderRadius: 3,
  boxSizing: "border-box",
};

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1240, margin: "24px auto 0", width: "100%" },
  panel: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  title: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  row: { display: "flex", gap: theme.spacing(1.5), alignItems: "flex-end" },
  label: { fontSize: 11, display: "block", marginBottom: 1 },
  // stretch + flex columns: both sides take the height of the taller one and
  // their bodies fill it, so the bottoms line up regardless of header height.
  split: { display: "flex", gap: theme.spacing(2), alignItems: "stretch" },
  col: { display: "flex", flexDirection: "column", minWidth: 0 },
  // Fixed height so the two headers occupy the same space — the toolbar makes
  // the right one taller otherwise, and the bodies start at different heights.
  colHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    height: 30,
    marginBottom: theme.spacing(0.5),
  },
  colTitle: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  code: {
    flex: 1,
    minHeight: 460,
    fontFamily: "monospace",
    fontSize: 11,
    padding: 8,
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: 3,
    resize: "vertical",
  },
  /**
   * Edited in place rather than through a rich-text component. Quill (used on
   * the watch form) has no table support and would flatten this block to
   * paragraphs on load; editing the live DOM keeps the table and its inline
   * styles intact.
   */
  editable: {
    outline: "none",
    "&:focus": { boxShadow: "0 0 0 2px rgba(21,101,192,0.35)" },
  },
  toolbarBtn: {
    minWidth: 30,
    height: 24,
    fontSize: 12,
    padding: "0 6px",
  },
  preview: {
    flex: 1,
    padding: theme.spacing(1),
    background: "#fff",
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 3,
    overflow: "auto",
  },
  error: { color: theme.palette.error.main },
  notice: { color: theme.palette.success.main },
}));

export function PreownedGeneratorPage() {
  const classes = useStyles();
  const [stockId, setStockId] = useState("");
  const [specialText, setSpecialText] = useState("");
  const [html, setHtml] = useState("");
  const [item, setItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const editorRef = useRef(null);

  const generate = useCallback(async () => {
    const id = stockId.trim();
    if (!id) return;
    setBusy(true);
    setError("");
    setNotice("");
    const { data, error: err } = await authGet(
      `/preowned/by-stock-id?stock_id=${encodeURIComponent(id)}`,
    );
    setBusy(false);
    if (err) return setError(err.message || "Lookup failed");
    if (!data?.found) {
      setItem(null);
      setHtml("");
      return setError(`No pre-owned item with stock number ${id}.`);
    }
    setItem(data.item);
    setHtml(buildHtml(data.item, specialText));
  }, [stockId, specialText]);

  /**
   * innerHTML is written only when `html` differs from what's already in the
   * DOM. Writing on every render would reset the caret on each keystroke.
   */
  useEffect(() => {
    const el = editorRef.current;
    if (el && el.innerHTML !== html) el.innerHTML = html;
  }, [html]);

  const syncFromEditor = () => {
    if (editorRef.current) setHtml(editorRef.current.innerHTML);
  };

  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    syncFromEditor();
  };

  async function copy() {
    try {
      await navigator.clipboard.writeText(html);
      setNotice("HTML copied.");
      setTimeout(() => setNotice(""), 3000);
    } catch {
      setError("Couldn't copy — select the HTML and copy manually.");
    }
  }

  return (
    <Box p={3} className={classes.page}>
      <Typography variant="h5" gutterBottom>
        Email Generator
      </Typography>

      <Paper className={classes.panel}>
        <div className={classes.title}>Item</div>
        <div className={classes.row}>
          <div style={{ width: 150 }}>
            <label className={classes.label}>Stock No.</label>
            <input
              value={stockId}
              onChange={(e) => setStockId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && generate()}
              style={{ ...FIELD, width: "100%" }}
            />
          </div>
          <div style={{ width: 300 }}>
            <label className={classes.label}>Special Notes</label>
            <input
              value={specialText}
              onChange={(e) => setSpecialText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && generate()}
              style={{ ...FIELD, width: "100%" }}
            />
          </div>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={generate}
            disabled={busy}
            style={{ height: 24, fontSize: 12 }}
          >
            {busy ? "…" : "Generate"}
          </Button>
          {html && (
            <Button
              variant="outlined"
              size="small"
              onClick={copy}
              style={{ height: 24, fontSize: 12 }}
            >
              Copy HTML
            </Button>
          )}
          {item && (
            <Typography variant="body2" color="textSecondary">
              {item.brand} — {item.series || item.accessory_type} —{" "}
              {item.model_no}
              {isAccessoryStock(item.stock_id) ? " (accessory)" : ""}
            </Typography>
          )}
          {error && <Typography className={classes.error}>{error}</Typography>}
          {notice && (
            <Typography className={classes.notice}>{notice}</Typography>
          )}
        </div>
      </Paper>

      {!!html && (
        <Paper className={classes.panel}>
          <div className={classes.split}>
            <div className={classes.col} style={{ flex: 1 }}>
              <div className={classes.colHeader}>
                <span className={classes.colTitle}>HTML source</span>
              </div>
              <textarea
                className={classes.code}
                value={html}
                onChange={(e) => setHtml(e.target.value)}
                style={{ width: "100%" }}
              />
            </div>

            <div className={classes.col} style={{ width: 340, flexShrink: 0 }}>
              <div
                className={classes.colHeader}
                style={{ justifyContent: "center" }}
              >
                <span className={classes.colTitle} style={{ marginRight: 10 }}>
                  Edit Preview
                </span>
                {[
                  ["bold", "B", { fontWeight: 700 }],
                  ["italic", "I", { fontStyle: "italic" }],
                  ["underline", "U", { textDecoration: "underline" }],
                ].map(([cmd, glyph, style]) => (
                  <Button
                    key={cmd}
                    size="small"
                    variant="outlined"
                    className={classes.toolbarBtn}
                    style={style}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => exec(cmd)}
                  >
                    {glyph}
                  </Button>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.toolbarBtn}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => exec("removeFormat")}
                >
                  Clear
                </Button>
              </div>
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={syncFromEditor}
                className={`${classes.preview} ${classes.editable}`}
              />
            </div>
          </div>
        </Paper>
      )}
    </Box>
  );
}
export default PreownedGeneratorPage;